import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  COUNTRY_CODE_OPTIONS,
  COUNTRY_NAME_OPTIONS,
  countryCodeToName,
  countryNameToCode,
} from '@/shared/lib/countries'
import { resolvePostcodeFill } from '@/shared/lib/postcode-lookup'
import { cn } from '@/shared/lib/utils'
import {
  DEFAULT_REQUIRED_ADDRESS_COMPONENTS,
  EMPTY_MAILING_ADDRESS,
  toMailingAddress,
  type MailingAddress,
} from '@/shared/lib/formatting/address'
import {
  useAddressLookup,
  type AddressSuggestion,
} from '@/shared/components/ui/address-lookup-context'
import { useAddressAutofill } from '@/shared/components/ui/address-autofill-context'

// ---------------------------------------------------------------------------
// The ONE platform-wide address capture: search-first with autocomplete, a
// labeled manual-entry modal (postcode-first with zip pre-fill), and a
// single-line summary. Lookup is provided by the nearest `AddressLookupProvider`
// so the same component works on every surface (admin registration wizard,
// transfers, inspections, entity registration, the claims multi-item editor,
// and the consumer app). Formerly the admin-only registration-wizard component.
// ---------------------------------------------------------------------------

/** Single-line summary string. */
function formatMailingAddress(address: MailingAddress | null | undefined): string {
  if (!address) return ''
  return [
    address.line_1,
    address.line_2,
    address.town_or_city,
    address.county,
    address.postcode,
    address.country,
  ]
    .filter((part): part is string => !!part?.trim())
    .join(', ')
}

interface AddressCaptureProps {
  value: MailingAddress | null
  onChange: (next: MailingAddress | null) => void
  error?: string
  placeholder?: string
  searchInputName?: string
  testId?: string
  summaryLabel?: string
  enterManualLabel?: string
  enterTitle?: string
  editTitle?: string
  /**
   * Address lookup provider the field opts into (e.g. ``google_places``).
   * Forwarded to the lookup endpoints; omitting it uses the org's configured
   * provider.
   */
  lookupProvider?: string
  /** Country pre-selected in the manual modal when none is set yet. */
  defaultCountry?: string
  /**
   * Subfield names that must be filled before the manual-entry modal can be
   * saved. Names match the manual field keys: ``postcode``, ``line_1``,
   * ``line_2``, ``town_or_city``, ``county``, ``country``. When omitted the
   * platform default applies (``postcode``, ``line_1``, ``town_or_city``).
   * Tenants tune this purely through the address attribute's
   * ``configuration_data.required_components`` - no per-tenant code.
   */
  requiredComponents?: string[]
  /**
   * Notified with the trimmed search term the user has typed but NOT yet
   * resolved into an address (empty once an address is committed or the box is
   * cleared). Surfaces the otherwise-local search text so a parent form can
   * treat "typed but never selected / entered manually" as an unresolved value
   * and block submission, instead of silently discarding it.
   */
  onPendingChange?: (pendingTerm: string) => void
}

const linkCx =
  'h-auto cursor-pointer border-none bg-transparent p-0 text-[11px] font-light'

export function AddressCapture({
  value,
  onChange,
  error,
  placeholder = 'Start typing an address or postcode',
  searchInputName = 'address-search',
  testId,
  summaryLabel = 'Address',
  enterManualLabel = 'Enter address manually',
  enterTitle = 'Enter address',
  editTitle = 'Edit address',
  // ``lookupProvider`` is intentionally not consumed: provider selection is a
  // server-side decision (the org's own address integration), so a field-level
  // pin no longer overrides it. Kept on the props for backward compatibility.
  defaultCountry,
  requiredComponents,
  onPendingChange,
}: AddressCaptureProps) {
  const { t } = useTranslation()
  const lookupApi = useAddressLookup()
  // Tenant postcode-autofill gate (on platform-wide); Teknor opts out.
  const autofillEnabled = useAddressAutofill()
  const [addressTerm, setAddressTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [resolving, setResolving] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  // The ISO alpha-2 country the search is biased to, and the full list of
  // countries the org's address integration offers. Both come from the lookup
  // capabilities (integration settings); the picker shows only when more than
  // one is offered, so a single-country tenant sees exactly today's UI.
  const [searchCountry, setSearchCountry] = useState('')
  const [countries, setCountries] = useState<string[]>([])
  // Multi-entry group drill-down: when the user selects a *group* suggestion
  // (a building that stands for many units) we replace the suggestion list with
  // its member units rather than resolving. `drillParent` is the group we are
  // inside; `unitSuggestions` are its members.
  const [drillParent, setDrillParent] = useState<AddressSuggestion | null>(null)
  const [unitSuggestions, setUnitSuggestions] = useState<AddressSuggestion[]>([])
  const [expanding, setExpanding] = useState(false)
  // Surfaced when a resolve/expand call fails, so the user is told to retry or
  // enter the address manually rather than being left with a vanished spinner.
  const [actionError, setActionError] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  )
  const formattedAddress = formatMailingAddress(value)

  // Country picker options: the offered ISO codes mapped to their names, in the
  // integration's order (default first). Unknown codes fall back to the raw code
  // so the picker never silently drops a configured country.
  const countryOptions = useMemo(
    () =>
      countries.map(
        (code) =>
          COUNTRY_CODE_OPTIONS.find((o) => o.value === code) ?? {
            value: code,
            label: code,
          },
      ),
    [countries],
  )

  // A search term the user has typed but not yet resolved into a committed
  // address. While non-empty it means "the user intends to give an address but
  // hasn't selected a suggestion or entered it manually" - callers use this to
  // block submission so the text is never silently discarded. Only notify on an
  // actual change (the callback identity may churn each parent render, and the
  // callback may write form state - re-firing on an unchanged term would loop).
  const pendingTerm = value ? '' : addressTerm.trim()
  const lastReportedPendingRef = useRef<string | null>(null)
  useEffect(() => {
    if (lastReportedPendingRef.current === pendingTerm) return
    lastReportedPendingRef.current = pendingTerm
    onPendingChange?.(pendingTerm)
  }, [pendingTerm, onPendingChange])

  // Load the offered countries + default from the integration settings. Absent
  // (surface without capabilities, or a single-country tenant) -> no picker, and
  // the search uses the org default country server-side, exactly as before.
  useEffect(() => {
    if (!lookupApi?.capabilities) return
    let cancelled = false
    lookupApi
      .capabilities()
      .then((caps) => {
        if (cancelled) return
        setCountries(caps.countries ?? [])
        // Seed from the org default (ISO code); fall back to the field's default
        // country. Never clobber a country the user already picked.
        setSearchCountry(
          (prev) =>
            prev ||
            caps.defaultCountry ||
            (defaultCountry ? countryNameToCode(defaultCountry) : ''),
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [lookupApi, defaultCountry])

  // Debounce the term so autocomplete fires once the user pauses.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(addressTerm.trim()), 250)
    return () => clearTimeout(handle)
  }, [addressTerm])

  // Fetch suggestions for the debounced term via the lookup context, biased to
  // the selected search country. A new term/country is always a fresh top-level
  // search, so any group drill-down is reset here.
  useEffect(() => {
    setDrillParent(null)
    setUnitSuggestions([])
    setActionError(null)
    if (!lookupApi || debouncedTerm.length < 3) {
      setSuggestions([])
      return
    }
    let cancelled = false
    lookupApi
      .autocomplete(debouncedTerm, searchCountry || undefined)
      .then((next) => {
        if (!cancelled) setSuggestions(next)
      })
      .catch(() => {
        if (!cancelled) setSuggestions([])
      })
    return () => {
      cancelled = true
    }
  }, [lookupApi, debouncedTerm, searchCountry])

  const manualFields = useMemo<FormField[]>(() => {
    // Default requiredness matches the long-standing platform behaviour; a
    // tenant can widen it via `configuration_data.required_components`. Labels
    // come from the translation layer so a tenant can relabel them per locale
    // (e.g. Teknor: Town / City -> City, County -> State / Province) without a
    // per-tenant code branch.
    const requiredSet = new Set<string>(
      requiredComponents && requiredComponents.length > 0
        ? requiredComponents
        : DEFAULT_REQUIRED_ADDRESS_COMPONENTS,
    )
    const requiredMessage = (label: string) =>
      t('validation.fieldRequired', '{{field}} is required', { field: label })
    const postcodeLabel = t('wizard.postcode', 'Postcode')
    const line1Label = t('wizard.addressLine1', 'Address Line 1')
    const line2Label = t('wizard.addressLine2', 'Address Line 2')
    const townCityLabel = t('wizard.townCity', 'Town / City')
    const countyLabel = t('wizard.county', 'County')
    const countryLabel = t('wizard.country', 'Country')

    return [
      {
        type: 'text',
        name: 'postcode',
        label: postcodeLabel,
        placeholder: postcodeLabel,
        value: value?.postcode ?? '',
        required: requiredSet.has('postcode') ? requiredMessage(postcodeLabel) : undefined,
        onBlur: async (val, formContext) => {
          if (!autofillEnabled) return
          if (!val || !formContext) return
          const result = await resolvePostcodeFill(
            String(val),
            lookupApi?.lookupPostcode,
          )
          if (!result) return
          // Compare by ISO code so a name-vs-code mismatch (e.g. a field whose
          // country was seeded as "US" while the lookup returns "United
          // States") does not silently suppress the fill. countryNameToCode
          // passes codes through unchanged, so it normalises either shape.
          const currentCountry = (formContext.values as Record<string, unknown>).country as string
          if (
            currentCountry &&
            result.country &&
            countryNameToCode(currentCountry) !== countryNameToCode(result.country)
          )
            return
          if (result.city) formContext.setFieldValue('town_or_city', result.city)
          if (result.state) formContext.setFieldValue('county', result.state)
          if (result.country) formContext.setFieldValue('country', result.country)
        },
      },
      {
        type: 'text',
        name: 'line_1',
        label: line1Label,
        placeholder: line1Label,
        value: value?.line_1 ?? '',
        required: requiredSet.has('line_1') ? requiredMessage(line1Label) : undefined,
      },
      {
        type: 'text',
        name: 'line_2',
        label: line2Label,
        placeholder: line2Label,
        value: value?.line_2 ?? '',
        required: requiredSet.has('line_2') ? requiredMessage(line2Label) : undefined,
      },
      {
        type: 'text',
        name: 'town_or_city',
        label: townCityLabel,
        placeholder: townCityLabel,
        value: value?.town_or_city ?? '',
        required: requiredSet.has('town_or_city') ? requiredMessage(townCityLabel) : undefined,
      },
      {
        type: 'text',
        name: 'county',
        label: countyLabel,
        placeholder: countyLabel,
        value: value?.county ?? '',
        required: requiredSet.has('county') ? requiredMessage(countyLabel) : undefined,
      },
      {
        type: 'select',
        name: 'country',
        label: countryLabel,
        placeholder: countryLabel,
        value: value?.country ?? '',
        options: COUNTRY_NAME_OPTIONS,
        required: requiredSet.has('country') ? requiredMessage(countryLabel) : undefined,
      },
    ]
  }, [value, requiredComponents, t, autofillEnabled, lookupApi])

  const manualFormDeps = useMemo(() => [manualOpen, value], [manualOpen, value])

  const { Form: ManualAddressForm, ref: manualFormRef } = useForm(
    {
      name: `${searchInputName}-manual-form`,
      submit: 'footer',
      fields: manualFields,
    },
    async (values) => {
      onChange({
        line_1: String(values.line_1 ?? ''),
        line_2: String(values.line_2 ?? ''),
        town_or_city: String(values.town_or_city ?? ''),
        county: String(values.county ?? ''),
        postcode: String(values.postcode ?? ''),
        country: String(values.country ?? ''),
      })
      setAddressTerm('')
      setManualOpen(false)
    },
    manualFormDeps,
  )

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (dropdownRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setShowSuggestions(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  useEffect(() => {
    if (!showSuggestions) {
      setMenuPos(null)
      return
    }
    function reposition() {
      const el = searchWrapRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [showSuggestions, suggestions, drillParent, unitSuggestions])

  async function handleSelectSuggestion(suggestion: AddressSuggestion) {
    if (!lookupApi) return
    // A group (an apartment/condo building) does not resolve to one address -
    // drill into its member units and let the user pick one. Common for urban
    // Canadian addresses, so this is the normal path, not an edge case.
    setActionError(null)
    const isGroup = (suggestion.entries ?? 1) > 1
    if (isGroup && lookupApi.expand) {
      setExpanding(true)
      try {
        const units = await lookupApi.expand(suggestion.id)
        setUnitSuggestions(units)
        setDrillParent(suggestion)
      } catch {
        // Tell the user the drill-down failed rather than leaving them staring
        // at a spinner that silently vanished (repo style: handle error states).
        setActionError(
          t(
            'address.expandFailed',
            "Couldn't load units - try again or enter the address manually.",
          ),
        )
      } finally {
        setExpanding(false)
      }
      return
    }

    setShowSuggestions(false)
    setResolving(true)
    try {
      const resolved = await lookupApi.resolve(suggestion.id, addressTerm)
      onChange(toMailingAddress(resolved))
      setAddressTerm('')
      setDrillParent(null)
      setUnitSuggestions([])
      setManualOpen(false)
    } catch {
      // Surface the failure so the user can retry or switch to manual entry,
      // instead of the resolving spinner just disappearing with no feedback.
      setActionError(
        t(
          'address.resolveFailed',
          "Couldn't resolve that address - try again or enter it manually.",
        ),
      )
    } finally {
      setResolving(false)
    }
  }

  function handleBackToResults() {
    setDrillParent(null)
    setUnitSuggestions([])
  }

  function openManual() {
    // The country <Select> is keyed by country NAME (COUNTRY_NAME_OPTIONS), so
    // normalise a possibly-ISO-code default ("US" -> "United States") or it
    // renders blank and never matches the postcode-lookup country. Seed from the
    // field's own default only - NOT the search-country picker: the picker biases
    // the address *search*, but seeding the manual dialog from it would override
    // the field default and break the postcode->city autofill's country match
    // (e.g. a GB org default vs a US ZIP), which is exactly what WPS-126 guards.
    manualFormRef.current?.resetForm({
      values: {
        ...EMPTY_MAILING_ADDRESS,
        country: defaultCountry ? countryCodeToName(defaultCountry) : '',
        ...(value ?? {}),
      },
    })
    setManualOpen(true)
  }

  function searchInstead() {
    onChange(null)
    setAddressTerm('')
    setManualOpen(false)
  }

  return (
    <div data-testid={testId}>
      {!value && (
        <div className="relative" ref={dropdownRef}>
          {/* Country + search form ONE composite control (like PhoneInput's
              country-code prefix), and only when the address integration offers
              more than one country. A single-country tenant sees just the search
              box, exactly as before. */}
          <div
            className={cn(
              countryOptions.length > 1 && 'flex items-stretch gap-2',
            )}
          >
            {countryOptions.length > 1 && (
              <Select
                value={searchCountry}
                onValueChange={(next) => {
                  setSearchCountry(next)
                  handleBackToResults()
                }}
              >
                <SelectTrigger
                  aria-label={t('address.searchCountry', 'Country')}
                  className="h-9! w-[88px] shrink-0 text-[13px]"
                >
                  {/* Show the compact ISO code in the prefix (the dropdown lists
                      full names), mirroring the phone input's "US (+1)" prefix. */}
                  {searchCountry || (
                    <SelectValue
                      placeholder={t('address.searchCountry', 'Country')}
                    />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {countryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div
              ref={searchWrapRef}
              className={cn(countryOptions.length > 1 && 'min-w-0 flex-1')}
            >
              <SearchInput
              value={addressTerm}
              onChange={(next) => {
                setAddressTerm(next)
                setShowSuggestions(next.length >= 3)
              }}
              onFocus={() => {
                if (addressTerm.length >= 3) setShowSuggestions(true)
              }}
              placeholder={placeholder}
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
              name={searchInputName}
              className={`h-9 text-[13px] ${error ? 'border-destructive' : ''}`}
              />
            </div>
          </div>
          {error ? (
            <p className="mt-1 text-xs text-[var(--status-error)]">{error}</p>
          ) : null}

          {showSuggestions &&
            (suggestions.length > 0 || drillParent) &&
            menuPos &&
            createPortal(
              <div
                ref={menuRef}
                style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
                className="fixed z-50 max-h-[240px] overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)]"
              >
                {drillParent ? (
                  <>
                    {/* Inside a group: a back row, the building we drilled into,
                        then its member units (each directly resolvable). */}
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={handleBackToResults}
                      className="h-auto w-full cursor-pointer justify-start gap-1 rounded-none border-b-[0.5px] border-b-[var(--rule)] px-3 py-2 text-left text-[12px] font-light text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)]"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      {t('address.backToResults', 'Back to results')}
                    </Button>
                    <p className="m-0 truncate px-3 py-1.5 text-[11px] font-light text-[var(--ink-3)]">
                      {drillParent.address}
                    </p>
                    {unitSuggestions.map((unit) => (
                      <Button
                        variant="ghost"
                        key={unit.id}
                        type="button"
                        onClick={() => handleSelectSuggestion(unit)}
                        className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-2 text-left text-[12px] font-light text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                      >
                        {unit.address}
                      </Button>
                    ))}
                    {!expanding && unitSuggestions.length === 0 && (
                      <p className="m-0 px-3 py-2 text-[11px] font-light text-[var(--ink-3)]">
                        {t('address.noUnitsFound', 'No units found')}
                      </p>
                    )}
                  </>
                ) : (
                  suggestions.map((suggestion) => {
                    const isGroup = (suggestion.entries ?? 1) > 1
                    return (
                      <Button
                        variant="ghost"
                        key={suggestion.id}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="h-auto w-full cursor-pointer justify-between gap-2 rounded-none px-3 py-2 text-left text-[12px] font-light text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {suggestion.address}
                        </span>
                        {isGroup && (
                          <span className="ml-2 flex shrink-0 items-center gap-1 text-[11px] text-[var(--ink-3)]">
                            {t('address.unitsCount', '{{count}} units', {
                              count: suggestion.entries,
                            })}
                            <ChevronRight className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </Button>
                    )
                  })
                )}
              </div>,
              document.body,
            )}

          {(resolving || expanding) && (
            <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">
              {expanding
                ? t('address.loadingUnits', 'Loading units...')
                : t('address.resolving', 'Resolving address...')}
            </p>
          )}

          {actionError && !resolving && !expanding && (
            <p className="mt-1.5 text-[11px] text-[var(--status-error)]">
              {actionError}
            </p>
          )}

          <Button variant="link" type="button" onClick={openManual} className={`mt-1.5 ${linkCx}`}>
            {enterManualLabel}
          </Button>
        </div>
      )}

      {value && (
        <div className="space-y-1.5">
          {summaryLabel && (
            <p className="m-0 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
              {summaryLabel}
            </p>
          )}
          <div className="flex items-center justify-between gap-3 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2">
            <p className="m-0 min-w-0 truncate text-[12px] font-light leading-[1.5] text-[var(--ink)]">
              {formattedAddress || (
                <span className="text-[var(--ink-3)]">No address details</span>
              )}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={openManual}
              aria-label={editTitle}
              className="-mr-1 shrink-0 font-light text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{value ? editTitle : enterTitle}</DialogTitle>
          </DialogHeader>
          <ManualAddressForm className="flex flex-col gap-3" />
          <DialogFooter>
            <Button variant="link" type="button" onClick={searchInstead} className={linkCx}>
              Search instead
            </Button>
            <Button variant="outline" type="button" onClick={() => setManualOpen(false)} className="font-light">
              Cancel
            </Button>
            <Button type="submit" form={`${searchInputName}-manual-form`} className="font-light">
              Save address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
